"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = void 0;
const common_1 = require("@finley-technologies/common");
const consolidateErrors_1 = require("./consolidateErrors");
const consolidateFieldNotFoundErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.field, // Group by field
    (error) => ({
        row: String(error.row),
        tapeNumber: String(error.tape),
    }), // Map to row data
    (field) => `Field [${field}] was not found`);
};
const consolidateDuplicateValuesErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.field, // Group by field
    (error) => ({
        row: String(error.row),
        tapeNumber: String(error.tape),
        value: error.value,
    }), // Map to row data
    (field) => `Duplicate values found for field [${field}]`);
};
const consolidateToStructuredErrors = (errors) => {
    const fieldNotFoundErrors = errors.filter((error) => error.type === 'stackOperationFieldNotFoundError');
    const duplicateValuesErrors = errors.filter((error) => error.type === 'stackDuplicateValueFoundError');
    // we want to consolidate the errors if there are too many of those.
    return [
        ...consolidateFieldNotFoundErrors(fieldNotFoundErrors),
        ...consolidateDuplicateValuesErrors(duplicateValuesErrors),
    ];
};
const getOptionalType = (type) => {
    switch (type) {
        case 'boolean':
        case 'optionalBoolean':
            return 'optionalBoolean';
        case 'date':
        case 'optionalDate':
            return 'optionalDate';
        case 'dateTime':
        case 'optionalDateTime':
            return 'optionalDateTime';
        case 'string':
        case 'optionalString':
            return 'optionalString';
        case 'float':
        case 'optionalFloat':
            return 'optionalFloat';
        case 'integer':
        case 'optionalInteger':
            return 'optionalInteger';
        // we do not expect following types in sanitized tapes
        // they reduce to float (or optionalFloat).
        case 'percentage':
        case 'optionalPercentage':
            return 'optionalPercentage';
        case 'currency':
        case 'optionalCurrency':
            return 'optionalCurrency';
        default:
            return (0, common_1.finleyAssertUnreachable)(type);
    }
};
const makeOptionalConfig = (fieldConfig) => {
    switch (fieldConfig.type) {
        // Note, this repeats the getOptionalType code above
        // but we cannot reuse getOptionalType here because
        // getOptionalType returns a union type, and TS
        // does not know it is good for the given fieldConfig.
        case 'boolean':
        case 'optionalBoolean':
            return {
                ...fieldConfig,
                type: 'optionalBoolean',
            };
        case 'date':
        case 'optionalDate':
            return {
                ...fieldConfig,
                type: 'optionalDate',
            };
        case 'dateTime':
        case 'optionalDateTime':
            return {
                ...fieldConfig,
                type: 'optionalDateTime',
            };
        case 'string':
        case 'optionalString':
            return {
                ...fieldConfig,
                type: 'optionalString',
            };
        case 'float':
        case 'optionalFloat':
            return {
                ...fieldConfig,
                type: 'optionalFloat',
            };
        case 'integer':
        case 'optionalInteger':
            return {
                ...fieldConfig,
                type: 'optionalInteger',
            };
        case 'percentage':
        case 'optionalPercentage':
            return {
                ...fieldConfig,
                type: 'optionalPercentage',
            };
        case 'currency':
        case 'optionalCurrency':
            return {
                ...fieldConfig,
                type: 'optionalCurrency',
            };
        default:
            return (0, common_1.finleyAssertUnreachable)(fieldConfig);
    }
};
// merges two fieldConfigs
// if the types are same, return the first one
// if one of them is optional, return the optional one
// if the types are different and not compatible, throw error
const getMergedFieldConfig = (fieldConfigA, fieldConfigB) => {
    if (fieldConfigA.type === fieldConfigB.type) {
        return fieldConfigA;
    }
    const typeA = fieldConfigA.type;
    const typeB = fieldConfigB.type;
    if (getOptionalType(typeA) === typeB) {
        return fieldConfigB;
    }
    if (getOptionalType(typeB) === typeA) {
        return fieldConfigA;
    }
    // the type are different and not compatible
    throw new Error(`Stack: [Field:${fieldConfigA.field}] has conflicting types: [${typeA}, ${typeB}]`);
};
const validateTransform = (transformation) => {
    try {
        const { ensureUniqueFields, srcDataTape, otherDataTapes } = transformation;
        // output tapeDefinition will include fields from all input tapes
        const allTapes = [srcDataTape, ...otherDataTapes];
        // any fields that exists in multiple tapes must have same data type.
        // if not, throw error
        const fieldNameToConfigMap = new Map();
        for (const tape of allTapes) {
            // ensure that all ensureUniqueFields are listed in the tapes
            for (const ensureUniqueField of ensureUniqueFields) {
                const uniqueFieldDefinition = tape.baseFields.find(({ field }) => field === ensureUniqueField);
                (0, common_1.finleyAssert)(uniqueFieldDefinition, `Stack: [Field:${ensureUniqueField}] not found in input tape(s)`);
                (0, common_1.finleyAssert)(uniqueFieldDefinition.type !==
                    getOptionalType(uniqueFieldDefinition.type), `Stack: [Field:${ensureUniqueField}] is [optional:${uniqueFieldDefinition.type}] in the input tape(s), but listed in the ensureUniqueFields`);
            }
            for (const baseFieldConfig of tape.baseFields) {
                const baseConfigs = fieldNameToConfigMap.get(baseFieldConfig.field) || [];
                baseConfigs.push(baseFieldConfig);
                fieldNameToConfigMap.set(baseFieldConfig.field, baseConfigs);
            }
        }
        // now generate the output tapeDefinition which includes all fields
        // if a field is listed in all tapes, its type stays the same
        // if a field is listed in some tapes, the type becomes `optional`
        const outputFields = [];
        for (const [_field, baseConfigs] of fieldNameToConfigMap) {
            let outputFieldConfig = baseConfigs[0];
            for (const baseConfig of baseConfigs) {
                outputFieldConfig = getMergedFieldConfig(outputFieldConfig, baseConfig);
            }
            // finally if not all tapes have this field, make it optional
            if (baseConfigs.length !== allTapes.length) {
                outputFieldConfig = makeOptionalConfig(outputFieldConfig);
            }
            outputFields.push(outputFieldConfig);
        }
        return {
            isValid: true,
            data: {
                baseFields: outputFields,
                delimiter: srcDataTape.delimiter,
                sanitizedFieldNames: srcDataTape.sanitizedFieldNames,
            },
        };
    }
    catch (error) {
        // caller requires that we throw an array of MetadataError.
        const errors = [];
        errors.push({
            type: 'metadataValidation',
            message: error instanceof Error
                ? error.message
                : 'Unexpected Error in Stack:getOutputDataTape',
        });
        return {
            isValid: false,
            errors,
        };
    }
};
const performTransform = async (params) => {
    const { dataTapeBuffers, transformation } = params;
    const internalErrors = [];
    const tapeDefinitionResult = validateTransform(transformation);
    (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, `Stack:validateTransform failed`);
    const outputTapeDefinition = tapeDefinitionResult.data;
    (0, common_1.finleyAssert)(outputTapeDefinition, `Stack:outputTapeDefinition is null`);
    const allFields = outputTapeDefinition.baseFields.map((field) => field.field);
    const delimiter = transformation.srcDataTape.delimiter;
    const outputCsvArray = [];
    const { ensureUniqueFields } = transformation;
    const uniqueFieldToValues = new Map(ensureUniqueFields.map((field) => [field, new Set()]));
    // eslint-disable-next-line no-restricted-syntax
    for (let bufferIndex = 0; bufferIndex < dataTapeBuffers.length; 
    // eslint-disable-next-line no-plusplus
    bufferIndex++) {
        const csvBuffer = dataTapeBuffers[bufferIndex];
        const { data } = common_1.csvConversion.csvBufferToJsonArray({
            csvBuffer,
            delimiter,
        });
        // eslint-disable-next-line no-restricted-syntax, no-plusplus
        for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
            const row = data[rowIndex];
            // ensure that all ensureUniqueFields are unique
            for (const field of ensureUniqueFields) {
                const uniqueValues = uniqueFieldToValues.get(field) || new Set();
                if (!(field in row)) {
                    // This should never happen, since we work with sanitized tapes
                    // and we have `getOutputDataTape` ensured that all ensureUniqueFields are listed in
                    // all the tape definitions - and they are not optional fields.
                    internalErrors.push({
                        type: 'stackOperationFieldNotFoundError',
                        row: rowIndex + 1,
                        tape: bufferIndex + 1,
                        field,
                    });
                }
                else {
                    const value = String(row[field]);
                    if (uniqueValues.has(value)) {
                        internalErrors.push({
                            type: 'stackDuplicateValueFoundError',
                            row: rowIndex + 1,
                            tape: bufferIndex + 1,
                            field,
                            value,
                        });
                    }
                    else {
                        uniqueValues.add(value);
                        uniqueFieldToValues.set(field, uniqueValues);
                    }
                }
            }
            if (internalErrors.length === 0) {
                // while there are no errors, collet the data
                // once errors are encountered we stop collecting data
                // we want to add any missing fields to the row
                // so that all rows have all fields
                allFields.forEach((missingField) => {
                    if (!(missingField in row)) {
                        row[missingField] = '';
                    }
                });
                outputCsvArray.push(row);
            }
        }
    }
    if (internalErrors.length > 0) {
        return {
            isValid: false,
            errors: consolidateToStructuredErrors(internalErrors),
        };
    }
    const outputBuffer = common_1.csvConversion.csvArrayToFinleyBuffer({
        csvArray: outputCsvArray,
        newline: '\n',
    });
    return { isValid: true, data: { outputBuffer }, supplementals: [] };
};
exports.transformOperation = {
    operation: 'stack',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=stack-transform.js.map